/**
 * Auth store — keeps the current Medusa user in memory and exposes
 * login / logout / refresh helpers. The Medusa session cookie is the
 * source of truth; this store is just a cached projection of `/auth/user`.
 */
import { create } from 'zustand';
import { sdk } from '@/lib/sdk';
import { setSentryUser } from '@/lib/sentry';
import type { AdminUser } from '@/types';

type AuthState = {
  user: AdminUser | null;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
  initialize: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
};

/**
 * Pulls a status code off anything Medusa's JS SDK might throw. The SDK
 * exposes the underlying `Response` either directly on the error object
 * (`err.response.status`) or wraps it under `.cause` / `err.status`.
 */
const getErrorStatus = (err: unknown): number | undefined => {
  if (!err || typeof err !== 'object') return undefined;
  const e = err as { status?: number; response?: { status?: number }; cause?: { status?: number } };
  return e.status ?? e.response?.status ?? e.cause?.status;
};

/**
 * Backoff helper for transient refresh failures. 1s → 2s, capped at 8s.
 * We swallow the rejection — the caller is fire-and-forget.
 */
const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000];
const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: false,
  isInitialized: false,
  error: null,

  initialize: async () => {
    set({ isLoading: true, error: null });
    try {
      const { user } = await sdk.admin.user.me();
      const adminUser = (user ?? null) as AdminUser | null;
      setSentryUser(adminUser ? { id: adminUser.id, email: adminUser.email } : null);
      set({ user: adminUser, isLoading: false, isInitialized: true });
    } catch (err) {
      // A failed session check (network error, backend down) currently
      // renders as "logged out". Log it so operators can distinguish
      // real session loss from transient backend failure.
      console.error('[auth] initialize failed:', err);
      setSentryUser(null);
      set({ user: null, isLoading: false, isInitialized: true });
    }
  },

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      // `login(actor, method, payload)` — payload is an object
      // for the `emailpass` provider.
      await sdk.auth.login('user', 'emailpass', { email, password });
      const { user } = await sdk.admin.user.me();
      const adminUser = (user ?? null) as AdminUser | null;
      setSentryUser(adminUser ? { id: adminUser.id, email: adminUser.email } : null);
      set({
        user: adminUser,
        isLoading: false,
        isInitialized: true,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Échec de la connexion';
      setSentryUser(null);
      set({ isLoading: false, error: message });
      throw err;
    }
  },

  logout: async () => {
    set({ isLoading: true });
    try {
      await sdk.auth.logout();
    } catch {
      // ignore — we still want to clear local state
    }
    setSentryUser(null);
    set({ user: null, isLoading: false, isInitialized: true, error: null });
  },

  refresh: async () => {
    for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
      try {
        const { user } = await sdk.admin.user.me();
        const adminUser = (user ?? null) as AdminUser | null;
        setSentryUser(adminUser ? { id: adminUser.id, email: adminUser.email } : null);
        set({ user: adminUser });
        return;
      } catch (err) {
        const status = getErrorStatus(err);
        const isAuthError = status === 401 || status === 403;
        // 401 / 403 → the session is gone, nothing to retry. Log out.
        if (isAuthError) {
          setSentryUser(null);
          set({ user: null });
          return;
        }
        // No status (likely a network / CORS error) or 5xx → transient.
        // Retry with backoff. After the last attempt, give up quietly and
        // keep whatever user we had so the UI doesn't flicker to a logged-out
        // state on a flaky connection.
        if (attempt < RETRY_DELAYS_MS.length) {
          // `noUncheckedIndexedAccess` widens array element access to
          // `T | undefined`. The bounds check above guarantees the
          // slot exists, so the non-null assertion is safe.
          await sleep(RETRY_DELAYS_MS[attempt] as number);
          continue;
        }
        return;
      }
    }
  },
}));
